import { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import {
 collection, getDocs, query, orderBy, limit, where,
  doc, getDoc, updateDoc, increment, addDoc, setDoc, deleteDoc, onSnapshot
} from "firebase/firestore";

export default function MusicFeed({ user, onPlaySong, currentSong, onViewProfile, highlightedSongId, onClearHighlight }) {
  const [tab, setTab] = useState("discover");

  // Songs state
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSong, setSelectedSong] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
const [mentionedUsers, setMentionedUsers] = useState([]);
  const [bookmarkedSongs, setBookmarkedSongs] = useState([]);
const [replyText, setReplyText] = useState("");
  const [likedSongs, setLikedSongs] = useState([]);
  const [followedArtists, setFollowedArtists] = useState([]);

  // Communities state
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [postImage, setPostImage] = useState(null);
const [postImagePreview, setPostImagePreview] = useState("");
const [uploadingPost, setUploadingPost] = useState(false);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
const [uploadTitle, setUploadTitle] = useState("");
const [uploadFile, setUploadFile] = useState(null);
const [uploadCover, setUploadCover] = useState(null);
const [uploadCoverPreview, setUploadCoverPreview] = useState("");
const [uploading, setUploading] = useState(false);
const [uploadProgress, setUploadProgress] = useState("");
  const [newCommunityName, setNewCommunityName] = useState("");
  const [newCommunityDesc, setNewCommunityDesc] = useState("");
  const [newCommunityGenre, setNewCommunityGenre] = useState("");
  const [newCommunityType, setNewCommunityType] = useState("public");
  const [newCommunityPhoto, setNewCommunityPhoto] = useState("");
const [newCommunityPhotoFile, setNewCommunityPhotoFile] = useState(null);
const [newCommunityPhotoPreview, setNewCommunityPhotoPreview] = useState("");
const [uploadingCommunityPhoto, setUploadingCommunityPhoto] = useState(false);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Messages state
  const [conversations, setConversations] = useState([]);
  const [msgTab, setMsgTab] = useState("inbox");
const [requestConvs, setRequestConvs] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
const [otherTyping, setOtherTyping] = useState(false);
const typingTimeoutRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [doubleTapHeart, setDoubleTapHeart] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const messagesEndRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const genres = ["Pop", "Hip-Hop", "R&B", "Rock", "Jazz", "Electronic", "K-Pop", "Myanmar", "Christian", "Country", "Indie", "Other"];

  useEffect(() => {
    loadSongs();
    loadUserData();
    loadCommunities();
    loadConversations();
    return () => { if (unsubscribeRef.current) unsubscribeRef.current(); };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

 async function deletePost(songId) {
    try {
      await updateDoc(doc(db, "songs", songId), { hidden: true, deletedAt: new Date().toISOString() });
      setSongs(prev => prev.filter(s => s.id !== songId));
    } catch {
      alert("Failed to delete. Try again.");
    }
  }
  async function reportContent(contentId, contentType, reason) {
    try {
      await addDoc(collection(db, "reports"), {
        contentId,
        contentType,
        reason,
        reportedBy: user.uid,
        reporterEmail: user.email,
        createdAt: new Date().toISOString(),
        status: "pending",
      });
      alert("✅ Report submitted. Our team will review this content.");
    } catch {
      alert("Failed to submit report. Try again.");
    }
  }
  useEffect(() => {
    if (highlightedSongId && songs.length > 0) {
      const el = document.getElementById(`song-${highlightedSongId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => onClearHighlight && onClearHighlight(), 3000);
      }
    }
  }, [highlightedSongId, songs]);
  async function loadSongs() {
    setLoading(true);
    try {
      const q = query(collection(db, "songs"), orderBy("createdAt", "desc"), limit(30));
      const snap = await getDocs(q);
      setSongs(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => !s.hidden));
    } catch {}
    setLoading(false);
  }

  async function loadUserData() {
    try {
      const likesSnap = await getDocs(collection(db, "users", user.uid, "likes"));
      setLikedSongs(likesSnap.docs.map(d => d.id));
      const followSnap = await getDocs(collection(db, "users", user.uid, "following"));
      setFollowedArtists(followSnap.docs.map(d => d.id));
      const joinSnap = await getDocs(collection(db, "users", user.uid, "communities"));
      setJoinedCommunities(joinSnap.docs.map(d => d.id));
      const pendingSnap = await getDocs(collection(db, "users", user.uid, "pendingCommunities"));
      setPendingRequests(pendingSnap.docs.map(d => d.id));
      const bookmarkSnap = await getDocs(collection(db, "users", user.uid, "bookmarks"));
      setBookmarkedSongs(bookmarkSnap.docs.map(d => d.id));
    } catch {}
  }

async function loadCommunities() {
    try {
      const { getDocs, collection } = await import("firebase/firestore");
      const { db } = await import("./firebase");
      const snap = await getDocs(collection(db, "communities"));
      console.log("COMMUNITIES TEST - size:", snap.size, "docs:", snap.docs.length);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log("COMMUNITIES DATA:", JSON.stringify(data[0]));
      setCommunities(data);
    } catch(e) { 
      console.error("COMMUNITIES FAILED:", e.code, e.message); 
    }
  }
useEffect(() => {
    const q = query(collection(db, "users", user.uid, "conversations"), orderBy("lastMessageAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setConversations(all.filter(c => !c.isRequest));
      setRequestConvs(all.filter(c => c.isRequest));
    });
    return () => unsub();
  }, [user.uid]);

  async function loadConversations() {}
  // ── MESSAGES ──────────────────────────────────────────────────────────────

  async function searchUsers(q) {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const snap = await getDocs(collection(db, "artists"));
      setSearchResults(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.userId !== user.uid && a.artistName?.toLowerCase().includes(q.toLowerCase())).slice(0, 5));
    } catch {}
  }

  function getConvId(uid1, uid2) { return [uid1, uid2].sort().join("_"); }

  async function openConversation(otherUser) {
    setSearchQuery(""); setSearchResults([]);
    const convId = getConvId(user.uid, otherUser.userId || otherUser.id);
    const conv = { id: convId, otherUserId: otherUser.userId || otherUser.id, otherUserName: otherUser.artistName || otherUser.displayName || "User", otherUserPhoto: otherUser.photoUrl || otherUser.photoURL || "", lastMessage: "", lastMessageAt: new Date().toISOString(), unread: 0 };
    setSelectedConv(conv);
    await setDoc(doc(db, "users", user.uid, "conversations", convId), conv);
    if (unsubscribeRef.current) unsubscribeRef.current();
    const q = query(collection(db, "conversations", convId, "messages"), orderBy("createdAt", "asc"), limit(100));
    unsubscribeRef.current = onSnapshot(q, snap => setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    
    // Listen for typing indicator
    const otherUserId = otherUser.userId || otherUser.id;
    const typingUnsub = onSnapshot(doc(db, "conversations", convId, "typing", otherUserId), snap => {
      setOtherTyping(snap.exists() && snap.data()?.typing === true);
    });
    return () => { typingUnsub(); };
  }

async function acceptRequest(conv) {
    try {
      await setDoc(doc(db, "users", user.uid, "conversations", conv.id), { ...conv, isRequest: false }, { merge: true });
      setRequestConvs(prev => prev.filter(c => c.id !== conv.id));
      setConversations(prev => [...prev, { ...conv, isRequest: false }]);
      openConversation({ userId: conv.otherUserId, artistName: conv.otherUserName, photoUrl: conv.otherUserPhoto });
    } catch {}
  }

  async function declineRequest(convId) {
    try {
      await deleteDoc(doc(db, "users", user.uid, "conversations", convId));
      setRequestConvs(prev => prev.filter(c => c.id !== convId));
    } catch {}
  }
  async function sendMessage() {
    if (!newMessage.trim() || !selectedConv) return;
    const message = { text: newMessage.trim(), senderId: user.uid, senderName: user.displayName || user.email?.split("@")[0] || "User", senderPhoto: user.photoURL || "", createdAt: new Date().toISOString(), read: false };
    setNewMessage("");
    try {
      await addDoc(collection(db, "conversations", selectedConv.id, "messages"), message);
      // Send push notification to recipient
      try {
        const recipientToken = await getDoc(doc(db, "fcmTokens", selectedConv.otherUserId));
        if (recipientToken.exists()) {
          await addDoc(collection(db, "pushQueue"), {
            token: recipientToken.data().token,
            title: user.displayName || "New Message",
            body: message.text || "📷 Image",
            data: { type: "message", convId: selectedConv.id },
            createdAt: new Date().toISOString(),
          });
        }
      } catch {}
      const lastMsgData = { lastMessage: message.text, lastMessageAt: message.createdAt, lastSenderId: user.uid };
      await setDoc(doc(db, "users", user.uid, "conversations", selectedConv.id), { ...selectedConv, ...lastMsgData, isRequest: false });
      
      // If new conversation, send as request to recipient
      if (selectedConv.isRequest) {
        await setDoc(doc(db, "users", selectedConv.otherUserId, "conversations", selectedConv.id), { 
          ...selectedConv, 
          otherUserId: user.uid, 
          otherUserName: user.displayName || user.email?.split("@")[0] || "User", 
          otherUserPhoto: user.photoURL || "", 
          ...lastMsgData, 
          unread: 1,
          isRequest: true, // Recipient sees this as a request
          requestFrom: user.uid,
          requestFromName: user.displayName || user.email?.split("@")[0] || "User",
        });
        setSelectedConv(prev => ({ ...prev, isRequest: false }));
      } else {
        await setDoc(doc(db, "users", selectedConv.otherUserId, "conversations", selectedConv.id), { 
          ...selectedConv, 
          otherUserId: user.uid, 
          otherUserName: user.displayName || user.email?.split("@")[0] || "User", 
          otherUserPhoto: user.photoURL || "", 
          ...lastMsgData, 
          unread: 1 
        });
      }
    } catch {}
  }

  // ── COMMUNITIES ───────────────────────────────────────────────────────────

  async function handleJoinCommunity(community) {
    if (community.type === "private") {
      if (pendingRequests.includes(community.id)) {
        await deleteDoc(doc(db, "communities", community.id, "joinRequests", user.uid));
        await deleteDoc(doc(db, "users", user.uid, "pendingCommunities", community.id));
        setPendingRequests(prev => prev.filter(id => id !== community.id));
      } else {
        await setDoc(doc(db, "communities", community.id, "joinRequests", user.uid), { userId: user.uid, userName: user.displayName || user.email?.split("@")[0] || "User", userPhoto: user.photoURL || "", requestedAt: new Date().toISOString(), status: "pending" });
        await setDoc(doc(db, "users", user.uid, "pendingCommunities", community.id), { communityId: community.id, timestamp: new Date().toISOString() });
        setPendingRequests(prev => [...prev, community.id]);
        alert("Join request sent! Waiting for admin approval.");
      }
    } else {
      if (joinedCommunities.includes(community.id)) {
        await deleteDoc(doc(db, "users", user.uid, "communities", community.id));
        await deleteDoc(doc(db, "communities", community.id, "members", user.uid));
        try { await updateDoc(doc(db, "communities", community.id), { members: increment(-1) }); } catch {}
        setJoinedCommunities(prev => prev.filter(id => id !== community.id));
        setCommunities(prev => prev.map(c => c.id === community.id ? { ...c, members: (c.members || 1) - 1 } : c));
      } else {
        await setDoc(doc(db, "users", user.uid, "communities", community.id), { communityId: community.id, timestamp: new Date().toISOString() });
        await setDoc(doc(db, "communities", community.id, "members", user.uid), { userId: user.uid, userName: user.displayName || user.email?.split("@")[0], userPhoto: user.photoURL || "", joinedAt: new Date().toISOString(), role: "member" });
        try { await updateDoc(doc(db, "communities", community.id), { members: increment(1) }); } catch {}
        setJoinedCommunities(prev => [...prev, community.id]);
        setCommunities(prev => prev.map(c => c.id === community.id ? { ...c, members: (c.members || 0) + 1 } : c));
      }
    }
  }

  async function openCommunity(community) {
    if (community.type === "private" && !joinedCommunities.includes(community.id) && community.creatorId !== user.uid) {
      alert("This is a private community. Request to join first!"); return;
    }
    setSelectedCommunity(community);
    try {
      const q = query(collection(db, "communities", community.id, "posts"), orderBy("createdAt", "desc"), limit(30));
      const snap = await getDocs(q);
      setCommunityPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (community.creatorId === user.uid) {
        const reqSnap = await getDocs(collection(db, "communities", community.id, "joinRequests"));
        setJoinRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.status === "pending"));
        const membSnap = await getDocs(collection(db, "communities", community.id, "members"));
        setCommunityMembers(membSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    } catch {}
  }

  async function approveRequest(communityId, requestUser) {
    try {
      await setDoc(doc(db, "communities", communityId, "members", requestUser.userId), { userId: requestUser.userId, userName: requestUser.userName, userPhoto: requestUser.userPhoto, joinedAt: new Date().toISOString(), role: "member" });
      await setDoc(doc(db, "users", requestUser.userId, "communities", communityId), { communityId, timestamp: new Date().toISOString() });
      await deleteDoc(doc(db, "users", requestUser.userId, "pendingCommunities", communityId));
      await deleteDoc(doc(db, "communities", communityId, "joinRequests", requestUser.userId));
      await updateDoc(doc(db, "communities", communityId), { members: increment(1) });
      setJoinRequests(prev => prev.filter(r => r.userId !== requestUser.userId));
      setCommunityMembers(prev => [...prev, { id: requestUser.userId, ...requestUser }]);
      setCommunities(prev => prev.map(c => c.id === communityId ? { ...c, members: (c.members || 0) + 1 } : c));
    } catch { alert("Error approving request"); }
  }

  async function rejectRequest(communityId, requestUser) {
    try {
      await deleteDoc(doc(db, "communities", communityId, "joinRequests", requestUser.userId));
      await deleteDoc(doc(db, "users", requestUser.userId, "pendingCommunities", communityId));
      setJoinRequests(prev => prev.filter(r => r.userId !== requestUser.userId));
    } catch {}
  }

  async function removeMember(communityId, memberId) {
    if (!window.confirm("Remove this member?")) return;
    try {
      await deleteDoc(doc(db, "communities", communityId, "members", memberId));
      await deleteDoc(doc(db, "users", memberId, "communities", communityId));
      await updateDoc(doc(db, "communities", communityId), { members: increment(-1) });
      setCommunityMembers(prev => prev.filter(m => m.id !== memberId));
    } catch {}
  }

  async function deletePost(communityId, postId) {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deleteDoc(doc(db, "communities", communityId, "posts", postId));
      setCommunityPosts(prev => prev.filter(p => p.id !== postId));
    } catch {}
  }

  async function postToCommunity() {
  if (!newPost.trim() && !postImage) return;
  if (!selectedCommunity) return;
  if (!joinedCommunities.includes(selectedCommunity.id) && selectedCommunity.creatorId !== user.uid) { alert("Join this community first!"); return; }
  
  setUploadingPost(true);
  let imageUrl = "";
  
  try {
    // Upload image if selected
    if (postImage) {
      const formData = new FormData();
      formData.append("file", postImage);
      formData.append("upload_preset", "mymusic_unsigned");
      const res = await fetch("https://api.cloudinary.com/v1_1/dsczkplah/image/upload", {
        method: "POST", body: formData
      });
      const data = await res.json();
      imageUrl = data.secure_url;
    }

    const post = {
      text: newPost.trim(),
      imageUrl,
      userId: user.uid,
      userName: user.displayName || user.email?.split("@")[0] || "User",
      userPhoto: user.photoURL || "",
      likes: 0,
      createdAt: new Date().toISOString()
    };

    const ref = await addDoc(collection(db, "communities", selectedCommunity.id, "posts"), post);
    setCommunityPosts(prev => [{ id: ref.id, ...post }, ...prev]);
    setNewPost("");
    setPostImage(null);
    setPostImagePreview("");
  } catch { alert("Failed to post. Try again."); }
  setUploadingPost(false);
}
   async function handleUploadSong() {
  if (!uploadTitle.trim()) { alert("Please enter a title."); return; }
  if (!uploadFile) { alert("Please select a file."); return; }
  if (!uploadFile.type?.startsWith("video/") && !uploadCover) { alert("Please add a cover image for your song."); return; }
  // Check upload limit for free users
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    const isPremium = userDoc.exists() && userDoc.data().premium === true;
    if (!isPremium) {
      const q = query(collection(db, "songs"), where("uid", "==", user.uid));
      const snap = await getDocs(q);
      const activeUploads = snap.docs.filter(d => !d.data().hidden).length;
      if (activeUploads >= 5) {
        alert("⭐ Free accounts can upload up to 5 songs. Upgrade to Premium for unlimited uploads!");
        return;
      }
    }
  } catch {}

  // ── LEVEL 1: Text moderation ──────────────────────────────────────────────
  const spamKeywords = ["buy", "sell", "click here", "free money", "giveaway", "bitcoin", "crypto", "invest", "http://", "https://", "bit.ly", "www.", "discount", "promo", "earn money", "make money"];
  const titleLower = uploadTitle.toLowerCase();
  const hasSpam = spamKeywords.some(k => titleLower.includes(k));
  if (hasSpam) { alert("❌ Your title appears to contain spam or unrelated content. Please enter a valid music title."); return; }

  // Check minimum title length and music relevance
  if (uploadTitle.trim().length < 2) { alert("❌ Please enter a proper song title."); return; }

  // ── LEVEL 2: File type enforcement ───────────────────────────────────────
  const allowedTypes = ["audio/mpeg", "audio/wav", "audio/aac", "audio/ogg", "audio/mp4", "video/mp4", "video/quicktime", "video/x-m4v"];
  const allowedExtensions = [".mp3", ".wav", ".aac", ".ogg", ".mp4", ".mov", ".m4v"];
  const fileName = uploadFile.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
  const hasValidType = allowedTypes.includes(uploadFile.type) || uploadFile.type.startsWith("audio/") || uploadFile.type.startsWith("video/");
  if (!hasValidExtension && !hasValidType) { alert("❌ Invalid file type. Only music files (MP3, WAV, AAC) and videos (MP4, MOV) are allowed."); return; }

  // File size limit — 100MB
  const maxSize = 100 * 1024 * 1024;
  if (uploadFile.size > maxSize) { alert("❌ File too large. Maximum size is 100MB."); return; }

  setUploading(true);
  const isVideo = uploadFile.type.startsWith("video/");
  setUploadProgress(isVideo ? "Uploading video..." : "Uploading audio...");

  try {
    const CLOUD = "dsczkplah";
    const PRESET = "mymusic_unsigned";

    // Upload audio/video
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("upload_preset", PRESET);
    const mediaRes = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/video/upload`, { method: "POST", body: formData });
    const mediaData = await mediaRes.json();
    const mediaUrl = mediaData.secure_url;

    // For video — auto generate thumbnail from video if no cover uploaded
    let coverUrl = "";
    if (uploadCover) {
      setUploadProgress("Uploading cover art...");
      const coverForm = new FormData();
      coverForm.append("file", uploadCover);
      coverForm.append("upload_preset", PRESET);
      const coverRes = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD}/image/upload`, { method: "POST", body: coverForm });
      const coverData = await coverRes.json();
      coverUrl = coverData.secure_url;
    } else if (isVideo && mediaData.public_id) {
      // Auto thumbnail from video at 1 second
      coverUrl = `https://res.cloudinary.com/${CLOUD}/video/upload/so_1/${mediaData.public_id}.jpg`;
    }

    setUploadProgress("Saving...");
    const userName = user.displayName || user.email?.split("@")[0] || "User";

    // Check if uploader is a verified artist
    let isVerifiedArtist = false;
    try {
      const artistDoc = await getDoc(doc(db, "artists", user.uid));
      if (artistDoc.exists() && artistDoc.data().verified === true) {
        isVerifiedArtist = true;
      }
    } catch {}

    await addDoc(collection(db, "songs"), {
      title: uploadTitle.trim(),
      artistId: user.uid,
      artistName: userName,
      artistPhoto: user.photoURL || "",
      audioUrl: isVideo ? null : mediaUrl,
      videoUrl: isVideo ? mediaUrl : null,
      coverUrl,
      isVideo,
      likes: 0, plays: 0, comments: 0,
      createdAt: new Date().toISOString(),
      uid: user.uid,
      artistVerified: isVerifiedArtist,
    });

    setShowUpload(false);
    setUploadTitle(""); setUploadFile(null); setUploadCover(null); setUploadCoverPreview("");
    setUploadProgress("");
    loadSongs();
    alert(isVideo ? "🎬 Video uploaded successfully!" : "🎵 Song uploaded successfully!");
  } catch {
    alert("Upload failed. Try again.");
  }
  setUploading(false); setUploadProgress("");
}
 async function createCommunity() {
  if (!newCommunityName.trim()) return;
  setUploadingCommunityPhoto(true);
  try {
    let photoUrl = "";
    if (newCommunityPhotoFile) {
      const formData = new FormData();
      formData.append("file", newCommunityPhotoFile);
      formData.append("upload_preset", "mymusic_unsigned");
      const res = await fetch("https://api.cloudinary.com/v1_1/dsczkplah/image/upload", {
        method: "POST", body: formData
      });
      const data = await res.json();
      photoUrl = data.secure_url;
    }

    const community = {
      name: newCommunityName.trim(),
      description: newCommunityDesc.trim(),
      genre: newCommunityGenre,
      type: newCommunityType,
      photoUrl,
      members: 1,
      creatorId: user.uid,
      creatorName: user.displayName || user.email?.split("@")[0],
      createdAt: new Date().toISOString()
    };

    const ref = await addDoc(collection(db, "communities"), community);
    await setDoc(doc(db, "users", user.uid, "communities", ref.id), { communityId: ref.id, timestamp: new Date().toISOString() });
    await setDoc(doc(db, "communities", ref.id, "members", user.uid), { userId: user.uid, userName: user.displayName || user.email?.split("@")[0], userPhoto: user.photoURL || "", joinedAt: new Date().toISOString(), role: "admin" });
    setCommunities(prev => [{ id: ref.id, ...community }, ...prev]);
    setJoinedCommunities(prev => [...prev, ref.id]);
    setShowCreateCommunity(false);
    setNewCommunityName(""); setNewCommunityDesc(""); setNewCommunityGenre(""); setNewCommunityType("public");
    setNewCommunityPhoto(""); setNewCommunityPhotoFile(null); setNewCommunityPhotoPreview("");
  } catch { alert("Failed to create community. Try again."); }
  setUploadingCommunityPhoto(false);
}
  // ── SONGS ─────────────────────────────────────────────────────────────────

  async function toggleLike(song) {
    const likeRef = doc(db, "users", user.uid, "likes", song.id);
    if (likedSongs.includes(song.id)) {
      await deleteDoc(likeRef);
      try { await updateDoc(doc(db, "songs", song.id), { likes: increment(-1) }); } catch {}
      setLikedSongs(prev => prev.filter(id => id !== song.id));
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, likes: (s.likes || 1) - 1 } : s));
    } else {
     await setDoc(likeRef, { songId: song.id, timestamp: new Date().toISOString() });
      try { await updateDoc(doc(db, "songs", song.id), { likes: increment(1) }); } catch {}
      // Notify post owner
      if (song.uid && song.uid !== user.uid) {
        try {
          await addDoc(collection(db, "users", song.uid, "notifications"), {
            type: "like",
            message: `${user.displayName || "Someone"} liked your song "${song.title}"`,
            songId: song.id,
            songTitle: song.title,
            fromUserId: user.uid,
            fromUserName: user.displayName || "Someone",
            fromUserPhoto: user.photoURL || "",
            read: false,
            createdAt: new Date().toISOString(),
          });
        } catch {}
      }
      setLikedSongs(prev => [...prev, song.id]);
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, likes: (s.likes || 0) + 1 } : s));
    }
  }

  async function toggleFollow(artistId) {
    const followRef = doc(db, "users", user.uid, "following", artistId);
    if (followedArtists.includes(artistId)) {
      await deleteDoc(followRef);
      try { await updateDoc(doc(db, "artists", artistId), { followers: increment(-1) }); } catch {}
      setFollowedArtists(prev => prev.filter(id => id !== artistId));
    } else {
      await setDoc(followRef, { artistId, timestamp: new Date().toISOString() });
      try { await updateDoc(doc(db, "artists", artistId), { followers: increment(1) }); } catch {}
      setFollowedArtists(prev => [...prev, artistId]);
    }
  }

  async function playAndCount(song) {
    onPlaySong({ trackId: song.id, trackName: song.title, artistName: song.artistName, artworkUrl100: song.coverUrl || "", previewUrl: "", isOriginal: true, audioUrl: song.audioUrl }, []);
    try { await updateDoc(doc(db, "songs", song.id), { plays: increment(1) }); } catch {}
    setSongs(prev => prev.map(s => s.id === song.id ? { ...s, plays: (s.plays || 0) + 1 } : s));
  }

  async function openComments(song) {
    setSelectedSong(song);
    try {
      const q = query(collection(db, "songs", song.id, "comments"), orderBy("createdAt", "desc"), limit(20));
      const snap = await getDocs(q);
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {}
  }

  async function postComment() {
    if (!newComment.trim() || !selectedSong) return;
    const comment = { text: newComment.trim(), userId: user.uid, userName: user.displayName || user.email?.split("@")[0] || "User", userPhoto: user.photoURL || "", createdAt: new Date().toISOString() };
    try {
      const ref = await addDoc(collection(db, "songs", selectedSong.id, "comments"), comment);
      setComments(prev => [{ id: ref.id, ...comment }, ...prev]);
     try { await updateDoc(doc(db, "songs", selectedSong.id), { comments: increment(1), lastComment: { text: comment.text, userName: comment.userName } }); } catch {}
      setSongs(prev => prev.map(s => s.id === selectedSong.id ? { ...s, comments: (s.comments || 0) + 1, lastComment: { text: comment.text, userName: comment.userName } } : s));
      // Notify post owner
      if (selectedSong.uid && selectedSong.uid !== user.uid) {
        try {
          await addDoc(collection(db, "users", selectedSong.uid, "notifications"), {
            type: "comment",
            message: `${user.displayName || "Someone"} commented on your song "${selectedSong.title}": "${comment.text.slice(0, 50)}"`,
            songId: selectedSong.id,
            songTitle: selectedSong.title,
            fromUserId: user.uid,
            fromUserName: user.displayName || "Someone",
            fromUserPhoto: user.photoURL || "",
            read: false,
            createdAt: new Date().toISOString(),
          });
        } catch {}
      }
      // Notify mentioned users
      for (const mentionedUser of mentionedUsers) {
        if (mentionedUser.userId && mentionedUser.userId !== user.uid) {
          try {
            await addDoc(collection(db, "users", mentionedUser.userId, "notifications"), {
              type: "mention",
              message: `${user.displayName || "Someone"} mentioned you in a comment`,
              fromUserId: user.uid,
              fromUserName: user.displayName || "Someone",
              read: false,
              createdAt: new Date().toISOString(),
            });
          } catch {}
        }
      }
      setMentionedUsers([]);
      setNewComment("");
    } catch {}
  }

  function timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

const getTrendingScore = (song) => {
    const hoursAgo = (Date.now() - new Date(song.createdAt).getTime()) / (1000 * 60 * 60);
    const engagementScore = (song.likes || 0) * 3 + (song.comments || 0) * 2 + (song.plays || 0);
    const recencyScore = Math.max(0, 48 - hoursAgo); // boost posts under 48 hours old
    const genreMatch = likedSongs.some(id => songs.find(s => s.id === id)?.genre === song.genre) ? 10 : 0;
    return engagementScore + recencyScore + genreMatch;
  };

  const displaySongs = tab === "following"
    ? songs.filter(s => followedArtists.includes(s.artistId) || followedArtists.includes(s.uid))
    : [...songs].sort((a, b) => getTrendingScore(b) - getTrendingScore(a));
    const isAdmin = selectedCommunity?.creatorId === user.uid;

  // ── CHAT VIEW ─────────────────────────────────────────────────────────────
  if (selectedConv) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", display: "flex", flexDirection: "column", zIndex: 200 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: "1px solid #111", flexShrink: 0 }}>
          <button onClick={() => { setSelectedConv(null); if (unsubscribeRef.current) unsubscribeRef.current(); }} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#111", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {selectedConv.otherUserPhoto ? <img src={selectedConv.otherUserPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
          </div>
          <div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 700 }}>{selectedConv.otherUserName}</div>
            <div style={{ fontSize: 11, color: "#1db954" }}>🎵 Artist</div>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {messages.length === 0 && <div style={{ textAlign: "center", padding: "40px 0", color: "#444", fontSize: 13 }}>Start a conversation! 🎵</div>}
          {messages.map(msg => {
            const isMine = msg.senderId === user.uid;
            return (
              <div key={msg.id} style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-end" }}>
                {!isMine && <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{msg.senderPhoto ? <img src={msg.senderPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 12 }}>👤</span>}</div>}
                <div style={{ maxWidth: "72%" }}>
<div style={{ padding: msg.imageUrl ? 4 : "10px 14px", borderRadius: isMine ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: isMine ? "#1db954" : "#1a1a1a", color: isMine ? "#000" : "#fff", fontSize: 14, lineHeight: 1.5, overflow: "hidden" }}>
                    {msg.imageUrl ? <img src={msg.imageUrl} alt="" style={{ width: 200, height: 200, objectFit: "cover", borderRadius: 14, display: "block" }} /> : msg.text}
                  </div>
                  <div style={{ fontSize: 10, color: "#333", marginTop: 3, textAlign: isMine ? "right" : "left" }}>{timeAgo(msg.createdAt)}</div>
                </div>
              </div>
            );
          })}
         {otherTyping && (
              <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, alignItems: "flex-end" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#111", flexShrink: 0 }} />
                <div style={{ padding: "10px 14px", borderRadius: "18px 18px 18px 4px", background: "#1a1a1a" }}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {[0,1,2].map(i => (
                      <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#555", animation: `typing 1.2s ${i * 0.2}s infinite` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          <div ref={messagesEndRef} />
        </div>
        <div style={{ padding: "12px 16px 32px", borderTop: "1px solid #111", display: "flex", gap: 10, alignItems: "center" }}>
         <label style={{ cursor: "pointer", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" style={{ width: 22, height: 22, fill: "none", stroke: "#555", strokeWidth: 1.8 }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={async e => {
              const file = e.target.files[0];
              if (!file || !selectedConv) return;
              const formData = new FormData();
              formData.append("file", file);
              formData.append("upload_preset", "mymusic_unsigned");
              try {
                const r = await fetch("https://api.cloudinary.com/v1_1/dsczkplah/image/upload", { method: "POST", body: formData });
                const d = await r.json();
                const imgMsg = { text: "", imageUrl: d.secure_url, senderId: user.uid, senderName: user.displayName || "User", senderPhoto: user.photoURL || "", createdAt: new Date().toISOString(), read: false };
                await addDoc(collection(db, "conversations", selectedConv.id, "messages"), imgMsg);
                const lastMsgData = { lastMessage: "📷 Image", lastMessageAt: imgMsg.createdAt, lastSenderId: user.uid };
                await setDoc(doc(db, "users", user.uid, "conversations", selectedConv.id), { ...selectedConv, ...lastMsgData }, { merge: true });
                await setDoc(doc(db, "users", selectedConv.otherUserId, "conversations", selectedConv.id), { ...selectedConv, otherUserId: user.uid, otherUserName: user.displayName || "User", otherUserPhoto: user.photoURL || "", ...lastMsgData, unread: 1 }, { merge: true });
              } catch {}
            }} />
          </label>
          <input value={newMessage} onChange={e => {
              setNewMessage(e.target.value);
              if (!selectedConv) return;
              // Set typing indicator
              setDoc(doc(db, "conversations", selectedConv.id, "typing", user.uid), { typing: true, updatedAt: new Date().toISOString() }, { merge: true });
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = setTimeout(() => {
                setDoc(doc(db, "conversations", selectedConv.id, "typing", user.uid), { typing: false }, { merge: true });
              }, 2000);
            }} placeholder="Type a message..." onKeyDown={e => e.key === "Enter" && sendMessage()}
            style={{ flex: 1, padding: "12px 16px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
          <button onClick={sendMessage} style={{ width: 44, height: 44, borderRadius: "50%", border: "none", background: "#1db954", color: "#000", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: "none", stroke: "#000", strokeWidth: 2 }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </button>
        </div>
      </div>
    );
  }

  // ── COMMUNITY DETAIL ──────────────────────────────────────────────────────
  if (selectedCommunity) {
    return (
      <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 120 }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
       <div style={{ background: "linear-gradient(135deg, #0d1a2e, #000)", padding: "0 0 20px" }}>
  {/* Community cover photo */}
  {selectedCommunity.photoUrl && (
    <div style={{ width: "100%", height: 140, overflow: "hidden", position: "relative", marginBottom: 16 }}>
      <img src={selectedCommunity.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.8) 100%)" }} />
    </div>
  )}
  <div style={{ padding: "20px 16px 0" }}></div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={() => { setSelectedCommunity(null); setShowAdminPanel(false); }} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            {isAdmin && <button onClick={() => setShowAdminPanel(!showAdminPanel)} style={{ padding: "7px 14px", borderRadius: 50, border: "1px solid #f59e0b", background: showAdminPanel ? "#f59e0b" : "transparent", color: showAdminPanel ? "#000" : "#f59e0b", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>⚙️ Admin Panel</button>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>{selectedCommunity.name}</div>
            <div style={{ padding: "3px 8px", borderRadius: 50, background: selectedCommunity.type === "private" ? "rgba(233,20,41,0.2)" : "rgba(29,185,84,0.2)", color: selectedCommunity.type === "private" ? "#e91429" : "#1db954", fontSize: 10, fontWeight: 700 }}>{selectedCommunity.type === "private" ? "🔒 Private" : "🌍 Public"}</div>
          </div>
          <div style={{ fontSize: 13, color: "#555", marginBottom: 8 }}>{selectedCommunity.description}</div>
          <div style={{ fontSize: 12, color: "#444" }}>👥 {selectedCommunity.members || 0} members</div>
         
    </div>

        {isAdmin && showAdminPanel && (
          <div style={{ background: "#0a0a0a", borderBottom: "1px solid #1a1a1a", padding: 16 }}>
            {joinRequests.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 10, color: "#f59e0b" }}>⏳ Join Requests ({joinRequests.length})</div>
                {joinRequests.map(req => (
                  <div key={req.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #111" }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{req.userPhoto ? <img src={req.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}</div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{req.userName}</div><div style={{ fontSize: 11, color: "#555" }}>{timeAgo(req.requestedAt)}</div></div>
                    <button onClick={() => approveRequest(selectedCommunity.id, req)} style={{ padding: "6px 12px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer", marginRight: 6 }}>✓</button>
                    <button onClick={() => rejectRequest(selectedCommunity.id, req)} style={{ padding: "6px 12px", borderRadius: 50, border: "1px solid #e91429", background: "transparent", color: "#e91429", fontSize: 12, cursor: "pointer" }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 10 }}>👥 Members ({communityMembers.length})</div>
              {communityMembers.map(member => (
                <div key={member.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #111" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{member.userPhoto ? <img src={member.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}</div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{member.userName}</div><div style={{ fontSize: 11, color: member.role === "admin" ? "#f59e0b" : "#555" }}>{member.role === "admin" ? "👑 Admin" : "Member"}</div></div>
                  {member.id !== user.uid && <button onClick={() => removeMember(selectedCommunity.id, member.id)} style={{ padding: "5px 10px", borderRadius: 50, border: "1px solid #e91429", background: "transparent", color: "#e91429", fontSize: 11, cursor: "pointer" }}>Remove</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ padding: 16, borderBottom: "1px solid #111" }}>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}</div>
            <div style={{ flex: 1 }}>
  <textarea value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Share your thoughts..." style={{ width: "100%", padding: "10px 14px", borderRadius: 12, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", resize: "none", minHeight: 80 }} />
  
  {/* Image preview */}
  {postImagePreview && (
    <div style={{ position: "relative", marginTop: 8, display: "inline-block" }}>
      <img src={postImagePreview} alt="" style={{ width: 100, height: 100, borderRadius: 8, objectFit: "cover", border: "1px solid #2a2a2a" }} />
      <button onClick={() => { setPostImage(null); setPostImagePreview(""); }} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#e91429", border: "none", color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
    </div>
  )}

  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
    {/* Image attach button */}
    <label style={{ cursor: "pointer" }}>
      <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#1a1a1a", border: "1px solid #2a2a2a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "none", stroke: "#666", strokeWidth: 1.8 }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
      </div>
      <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
        const f = e.target.files[0];
        if (f) { setPostImage(f); setPostImagePreview(URL.createObjectURL(f)); }
      }} />
    </label>

    <button onClick={postToCommunity} disabled={uploadingPost} style={{ flex: 1, padding: "8px 20px", borderRadius: 50, border: "none", background: uploadingPost ? "#333" : "#1db954", color: uploadingPost ? "#666" : "#000", fontSize: 13, fontWeight: 700, cursor: uploadingPost ? "not-allowed" : "pointer" }}>
      {uploadingPost ? "Posting..." : "Post"}
    </button>
  </div>
</div>
          </div>
        </div>

        {communityPosts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 20px" }}><div style={{ fontSize: 48, marginBottom: 12 }}>💬</div><div style={{ fontSize: 14, color: "#555" }}>No posts yet. Start the conversation!</div></div>
        ) : communityPosts.map(post => (
          <div key={post.id} style={{ padding: 16, borderBottom: "1px solid #0a0a0a" }}>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{post.userPhoto ? <img src={post.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><div style={{ fontSize: 13, fontWeight: 600 }}>{post.userName}</div><div style={{ fontSize: 11, color: "#444" }}>{timeAgo(post.createdAt)}</div></div>
                  {isAdmin && <button onClick={() => deletePost(selectedCommunity.id, post.id)} style={{ background: "none", border: "none", color: "#e91429", fontSize: 12, cursor: "pointer" }}>🗑️</button>}
                </div>
              </div>
            </div>
{post.text && <div style={{ fontSize: 14, color: "#ccc", lineHeight: 1.6, marginBottom: post.imageUrl ? 10 : 0 }}>{post.text}</div>}
{post.imageUrl && (
  <img src={post.imageUrl} alt="" style={{ width: "100%", borderRadius: 10, objectFit: "cover", maxHeight: 300, display: "block", marginTop: 4 }} />
)}
          </div>
        ))}
      </div>
    );
  }

  // ── MAIN VIEW ─────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#000", color: "#fff", fontFamily: "'DM Sans',sans-serif", paddingBottom: 120 }}>
     <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap'); @keyframes spin{to{transform:rotate(360deg)}} @keyframes typing{0%,100%{transform:translateY(0);opacity:0.4}50%{transform:translateY(-4px);opacity:1}}`}</style>

      <div style={{ padding: "20px 16px 0", background: "linear-gradient(160deg, #0d2818 0%, #000 70%)" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
          {tab === "communities" ? "💬 Communities" : tab === "messages" ? "✉️ Messages" : "🎵 Discover"}
        </div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
          {tab === "communities" ? "Connect with music lovers worldwide" : tab === "messages" ? "Chat with artists" : "Original music from artists worldwide"}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
          {[["discover", "🌍 Discover"], ["following", "👥 Following"], ["communities", "💬 Groups"], ["messages", "✉️ Messages"], ["search", "👥 People"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "8px 14px", borderRadius: 50, border: tab === id ? "none" : "1px solid #2a2a2a", background: tab === id ? "#1db954" : "transparent", color: tab === id ? "#000" : "#666", fontSize: 12, fontWeight: tab === id ? 700 : 400, cursor: "pointer", flexShrink: 0 }}>
              {label}
            </button>
          ))}
        </div>
      </div>
{(tab === "discover" || tab === "following") && <button onClick={() => setShowUpload(true)} style={{ marginBottom: 16, width: "100%", padding: "12px 16px", borderRadius: 10, border: "1px solid #1a1a1a", background: "#111", color: "#fff", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" }}>
  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#000", border: "1px solid #2a2a2a", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
    {user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 14 }}>👤</span>}
  </div>
  <span style={{ flex: 1, fontSize: 14, color: "#444" }}>Upload your music...</span>
  <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#1db954", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: "none", stroke: "#000", strokeWidth: 2.5 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
  </div>
</button>
} 

      {/* MESSAGES TAB */}
      {/* SEARCH TAB */}
{tab === "search" && (
  <div style={{ padding: 16 }}>
    <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); searchUsers(e.target.value); }} placeholder="Search artists, communities..."
      style={{ width: "100%", padding: "11px 14px 11px 38px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", marginBottom: 16 }} />
    
    {/* Artist results */}
    {searchResults.length > 0 && (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12, color: "#555" }}>🎤 Artists</div>
        {searchResults.map(artist => (
          <div key={artist.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #1db954" }}>
              {artist.photoUrl ? <img src={artist.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>🎤</span>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{artist.artistName}</div>
              <div style={{ fontSize: 12, color: "#555" }}>{artist.genre || "Artist"} · {artist.followers || 0} followers</div>
            </div>
            <button onClick={() => toggleFollow(artist.userId || artist.id)} style={{ padding: "7px 14px", borderRadius: 50, border: followedArtists.includes(artist.userId || artist.id) ? "none" : "1px solid #2a2a2a", background: followedArtists.includes(artist.userId || artist.id) ? "#1db954" : "transparent", color: followedArtists.includes(artist.userId || artist.id) ? "#000" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {followedArtists.includes(artist.userId || artist.id) ? "✓" : "+ Follow"}
            </button>
          </div>
        ))}
      </div>
    )}

    {/* Community results */}
    {searchQuery.length > 1 && communities.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).length > 0 && (
      <div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, marginBottom: 12, color: "#555" }}>💬 Communities</div>
        {communities.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map(community => (
          <div key={community.id} onClick={() => openCommunity(community)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #1db954, #0a8f3c)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
  {community.photoUrl
    ? <img src={community.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    : community.genre === "Myanmar" ? "🇲🇲" : community.genre === "K-Pop" ? "🇰🇷" : community.genre === "Christian" ? "✝️" : community.genre === "Electronic" ? "🎛️" : "🎵"}
</div>
           
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{community.name}</div>
              <div style={{ fontSize: 12, color: "#555" }}>👥 {community.members || 0} members</div>
            </div>
          </div>
        ))}
      </div>
    )}

    {searchQuery.length === 0 && (
      <div style={{ textAlign: "center", padding: "48px 0" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Find Artists & Communities</div>
        <div style={{ fontSize: 13, color: "#555" }}>Search for artists, music communities and connect!</div>
      </div>
    )}
  </div>
)}
{tab === "messages" && (
        <div style={{ padding: 16 }}>
          {/* Inbox / Requests tabs */}
          <div style={{ display: "flex", background: "#0a0a0a", borderRadius: 10, padding: 4, marginBottom: 16 }}>
            {[["inbox", "Inbox", conversations.length], ["requests", "Requests", requestConvs.length]].map(([t, label, count]) => (
              <div key={t} onClick={() => setMsgTab(t)} style={{ flex: 1, textAlign: "center", padding: "8px 0", borderRadius: 8, background: msgTab === t ? "#111" : "transparent", fontSize: 13, fontWeight: msgTab === t ? 700 : 400, color: msgTab === t ? "#fff" : "#555", cursor: "pointer", position: "relative" }}>
                {label}
                {count > 0 && <span style={{ marginLeft: 6, background: t === "requests" ? "#e91429" : "#1db954", color: "#fff", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 50 }}>{count}</span>}
              </div>
            ))}
          </div>

          {/* Search */}
          <div style={{ position: "relative", marginBottom: 16 }}>
            <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); searchUsers(e.target.value); }} placeholder="Search artists to message..."
              style={{ width: "100%", padding: "11px 14px 11px 38px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
            <svg viewBox="0 0 24 24" style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, fill: "none", stroke: "#555", strokeWidth: 2 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            {searchResults.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1a1a1a", borderRadius: 12, padding: "6px 0", zIndex: 50, border: "1px solid #2a2a2a", marginTop: 4 }}>
                {searchResults.map(artist => (
                  <div key={artist.id} onClick={() => openConversation(artist)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{artist.photoUrl ? <img src={artist.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>🎤</span>}</div>
                    <div><div style={{ fontSize: 14, fontWeight: 600 }}>{artist.artistName}</div><div style={{ fontSize: 11, color: "#555" }}>{artist.genre || "Artist"}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Requests tab */}
          {msgTab === "requests" && (
            requestConvs.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📬</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 8 }}>No message requests</div>
                <div style={{ fontSize: 13, color: "#555" }}>When someone new messages you, it'll appear here first.</div>
              </div>
            ) : requestConvs.map(conv => (
              <div key={conv.id} style={{ background: "#111", borderRadius: 12, padding: 14, marginBottom: 10, border: "1px solid #1a1a1a" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#222", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {conv.otherUserPhoto ? <img src={conv.otherUserPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span>👤</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{conv.otherUserName}</div>
                    <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{conv.lastMessage}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => acceptRequest(conv)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>✓ Accept</button>
                  <button onClick={() => declineRequest(conv.id)} style={{ flex: 1, padding: "8px 0", borderRadius: 8, border: "1px solid #2a2a2a", background: "transparent", color: "#555", fontSize: 13, cursor: "pointer" }}>✕ Decline</button>
                </div>
              </div>
            ))
          )}

          {/* Inbox tab */}
          {msgTab === "inbox" && (
          <div>
          {conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>✉️</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No messages yet</div>
              <div style={{ fontSize: 13, color: "#555" }}>Search for artists above to start chatting!</div>
            </div>
          ) : conversations.map(conv => (
            <div key={conv.id} onClick={() => openConversation({ userId: conv.otherUserId, artistName: conv.otherUserName, photoUrl: conv.otherUserPhoto })}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 0", borderBottom: "1px solid #0a0a0a", cursor: "pointer" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                {conv.otherUserPhoto ? <img src={conv.otherUserPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>👤</span>}
                {conv.unread > 0 && <div style={{ position: "absolute", top: 0, right: 0, width: 14, height: 14, background: "#1db954", borderRadius: "50%", border: "2px solid #000", fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#000", fontWeight: 700 }}>{conv.unread}</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: conv.unread > 0 ? 700 : 500, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{conv.otherUserName}</div>
                <div style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{conv.lastMessage || "Start a conversation"}</div>
              </div>
              <div style={{ fontSize: 11, color: "#444", flexShrink: 0 }}>{conv.lastMessageAt ? timeAgo(conv.lastMessageAt) : ""}</div>
            </div>
          ))}
        </div>
      )}

    {tab === "communities" && <div style={{ padding: 20, color: "red", fontSize: 24 }}>TAB IS WORKING - {communities.length} communities</div>}
      {/* Create Community Modal */}
      {showCreateCommunity && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => setShowCreateCommunity(false)}>
          <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", border: "1px solid #2a2a2a", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Create Community</div>
           
{/* Community cover photo */}
<div style={{ marginBottom: 16 }}>
  <div style={{ fontSize: 11, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Cover Photo (Optional)</div>
  <label style={{ cursor: "pointer", display: "block" }}>
    <div style={{ width: "100%", height: 120, borderRadius: 12, border: "2px dashed #2a2a2a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a0a", position: "relative" }}>
      {newCommunityPhotoPreview ? (
        <>
          <img src={newCommunityPhotoPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 12, color: "#fff", fontWeight: 600 }}>Tap to change</span>
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center" }}>
          <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, fill: "none", stroke: "#444", strokeWidth: 1.5, display: "block", margin: "0 auto 8px" }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          <span style={{ fontSize: 12, color: "#444" }}>Add cover photo</span>
        </div>
      )}
    </div>
    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
      const f = e.target.files[0];
      if (f) { setNewCommunityPhotoFile(f); setNewCommunityPhotoPreview(URL.createObjectURL(f)); }
    }} />
  </label>
</div>
            {[{ label: "Name *", value: newCommunityName, set: setNewCommunityName, placeholder: "e.g. Myanmar Rock Fans" }, { label: "Description", value: newCommunityDesc, set: setNewCommunityDesc, placeholder: "What is this about?" }].map(f => (
              <div key={f.label} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>{f.label}</div>
                <input value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.placeholder} style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 6 }}>Genre</div>
              <select value={newCommunityGenre} onChange={e => setNewCommunityGenre(e.target.value)} style={{ width: "100%", padding: "11px 13px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontSize: 14, outline: "none" }}>
                <option value="">Select genre</option>
                {genres.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>Community Type</div>
              <div style={{ display: "flex", gap: 10 }}>
                {[["public", "🌍 Public", "Anyone can join"], ["private", "🔒 Private", "Admin approves"]].map(([type, label, desc]) => (
                  <div key={type} onClick={() => setNewCommunityType(type)} style={{ flex: 1, padding: 12, borderRadius: 12, border: `1px solid ${newCommunityType === type ? "#1db954" : "#2a2a2a"}`, background: newCommunityType === type ? "rgba(29,185,84,0.08)" : "#0a0a0a", cursor: "pointer" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3, color: newCommunityType === type ? "#1db954" : "#fff" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#555" }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={createCommunity} style={{ width: "100%", padding: 14, borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>Create Community</button>
            <button onClick={() => setShowCreateCommunity(false)} style={{ width: "100%", padding: 12, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>Cancel</button>
         </div>
          </div>
          )}
        </div>
      )}

      {/* SONGS FEED */}
   {(tab === "discover" || tab === "following") && (
        loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <div style={{ width: 32, height: 32, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          </div>
        ) : displaySongs.length === 0 ? (
          <div style={{ padding: "20px 16px" }}>
            {tab === "following" ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>👥</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>No music from followed artists</div>
                <div style={{ fontSize: 13, color: "#555" }}>Follow artists to see their music here!</div>
              </div>
            ) : (
              <div>
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🎶</div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 6 }}>Be the first to share your music!</div>
                  <div style={{ fontSize: 13, color: "#555", marginBottom: 20 }}>Upload a song or share something from Song Studio to get started.</div>
                  <button onClick={() => setShowUpload(true)} style={{
                    padding: "12px 28px", borderRadius: 50, border: "none",
                    background: "linear-gradient(135deg, #1db954, #17a345)", color: "#fff",
                    fontSize: 14, fontWeight: 700, cursor: "pointer"
                  }}>🎵 Upload Your First Song</button>
                </div>


              </div>
            )}
          </div>
        ) : displaySongs.map(song => (
         <div key={song.id} id={`song-${song.id}`} style={{ borderBottom: "1px solid #0a0a0a", transition: "background 0.5s", background: highlightedSongId === song.id ? "rgba(29,185,84,0.08)" : "transparent" }}>
            <div style={{ padding: "16px 16px 12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
<div onClick={() => onViewProfile(song.artistId)} style={{ width: 38, height: 38, borderRadius: "50%", background: "#111", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #1db954", cursor: "pointer" }}>{song.artistPhoto ? <img src={song.artistPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 16 }}>🎤</span>}</div>
               <div style={{ flex: 1 }}>
  <div onClick={() => onViewProfile(song.artistId)} style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
    {song.artistName}
    {song.verified && (
      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#1d9bf0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg viewBox="0 0 24 24" style={{ width: 9, height: 9, fill: "none", stroke: "#fff", strokeWidth: 3 }}><polyline points="20 6 9 17 4 12"/></svg>
      </div>
    )}
  </div>
  <div style={{ fontSize: 11, color: "#555" }}>{timeAgo(song.createdAt)} · {song.genre || "Music"}</div>
</div>
                <button onClick={() => toggleFollow(song.artistId)} style={{ padding: "6px 14px", borderRadius: 50, border: followedArtists.includes(song.artistId) ? "none" : "1px solid #2a2a2a", background: followedArtists.includes(song.artistId) ? "#1db954" : "transparent", color: followedArtists.includes(song.artistId) ? "#000" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{followedArtists.includes(song.artistId) ? "✓ Following" : "+ Follow"}</button>
                <button onClick={() => {
  const reasons = ["Spam", "Not music", "Inappropriate", "Fake content", "Copyright"];
  const reason = window.prompt(`Report this content:\n\n${reasons.map((r, i) => `${i+1}. ${r}`).join("\n")}\n\nEnter number (1-5):`);
  if (reason && ["1","2","3","4","5"].includes(reason.trim())) {
    reportContent(song.id, "song", reasons[parseInt(reason.trim()) - 1]);
  }
}} style={{ background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 16, padding: "4px 8px" }}>⚑</button>
             {user?.uid === song.artistId && (() => {
                  const postedAt = new Date(song.createdAt).getTime();
                  const oneWeek = 7 * 24 * 60 * 60 * 1000;
                  const canDelete = Date.now() - postedAt < oneWeek;
                  if (!canDelete) return null;
                  return (
                    <button onClick={() => {
                      if (window.confirm("Delete this post? This will remove it from the Feed.")) {
                        deletePost(song.id);
                      }
                    }} style={{ background: "none", border: "none", color: "#e91429", cursor: "pointer", fontSize: 16, padding: "4px 8px" }}>🗑️</button>
                  );
                })()}
              </div>
              <div style={{ position: "relative", marginBottom: 12, cursor: "pointer" }} 
  onClick={() => playAndCount(song)}
  onDoubleClick={(e) => {
    e.preventDefault();
    if (!likedSongs.includes(song.id)) toggleLike(song);
    setDoubleTapHeart(song.id);
    setTimeout(() => setDoubleTapHeart(null), 800);
  }}>
                <div style={{ width: "100%", paddingBottom: "100%", position: "relative", borderRadius: 12, overflow: "hidden", background: "#111" }}>
                  {song.isVideo && song.videoUrl ? (
  <video src={song.videoUrl} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} muted playsInline poster={song.coverUrl || ""} />
) : song.coverUrl ? (
  <img src={song.coverUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
) : (
  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0d2818, #1a4a2a)", fontSize: 64 }}>🎵</div>
)}
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)" }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: currentSong?.trackId === song.id ? "#1db954" : "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
{currentSong?.trackId === song.id ? (
  <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, fill: "#000" }}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
) : song.isVideo ? (
  <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, fill: "#000" }}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
) : (
  <svg viewBox="0 0 24 24" style={{ width: 24, height: 24, fill: "#000", marginLeft: 3 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
)}
                  
                  {doubleTapHeart === song.id && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                      <svg viewBox="0 0 24 24" style={{ width: 90, height: 90, fill: "#fff", animation: "heartPop 0.8s ease" }}>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                      <style>{`@keyframes heartPop { 0% { transform: scale(0); opacity: 0; } 30% { transform: scale(1.3); opacity: 1; } 50% { transform: scale(1); } 100% { transform: scale(1); opacity: 0; } }`}</style>
                    </div>
                  )}
                  

                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}><div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 2 }}>{song.title}</div><div style={{ fontSize: 12, color: "#555" }}>▶ {song.plays || 0} plays</div></div>
              {song.caption && (
                <div style={{ fontSize: 13, color: "#ccc", lineHeight: 1.5, marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, marginRight: 6 }}>{song.artistName}</span>
                  {song.caption}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                <button onClick={() => toggleLike(song)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: likedSongs.includes(song.id) ? "#e91429" : "#666", fontSize: 13 }}>
                  <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: likedSongs.includes(song.id) ? "#e91429" : "none", stroke: likedSongs.includes(song.id) ? "#e91429" : "#666", strokeWidth: 1.8 }}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  {song.likes || 0}
                </button>
                <button onClick={() => openComments(song)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 13 }}>
                  <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#666", strokeWidth: 1.8 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  {song.comments || 0}
                </button>
                <button onClick={() => { const text = `🎵 "${song.title}" by ${song.artistName}\nhttps://mymusicstream.online`; if (navigator.share) navigator.share({ title: song.title, text }); else { navigator.clipboard.writeText(text); alert("Copied!"); } }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 13 }}>
                  <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: "none", stroke: "#666", strokeWidth: 1.8 }}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  Share
                </button>
                <button onClick={async () => {
                  const bookmarkRef = doc(db, "users", user.uid, "bookmarks", song.id);
                  const isBookmarked = bookmarkedSongs.includes(song.id);
                  if (isBookmarked) {
                    await deleteDoc(bookmarkRef);
                    setBookmarkedSongs(prev => prev.filter(id => id !== song.id));
                  } else {
                    await setDoc(bookmarkRef, { songId: song.id, title: song.title, artistName: song.artistName, coverUrl: song.coverUrl || "", audioUrl: song.audioUrl || "", createdAt: new Date().toISOString() });
                    setBookmarkedSongs(prev => [...prev, song.id]);
                  }
                }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: bookmarkedSongs.includes(song.id) ? "#1db954" : "#666", fontSize: 13 }}>
                  <svg viewBox="0 0 24 24" style={{ width: 20, height: 20, fill: bookmarkedSongs.includes(song.id) ? "#1db954" : "none", stroke: bookmarkedSongs.includes(song.id) ? "#1db954" : "#666", strokeWidth: 1.8 }}><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                </button>
                {song.lastComment && (
                <div onClick={() => openComments(song)} style={{ marginTop: 10, fontSize: 13, color: "#999", cursor: "pointer", lineHeight: 1.5 }}>
                  <span style={{ fontWeight: 700, color: "#ccc", marginRight: 6 }}>{song.lastComment.userName}</span>
                  {song.lastComment.text}
                  {song.comments > 1 && <div style={{ fontSize: 12, color: "#444", marginTop: 4 }}>View all {song.comments} comments</div>}
                </div>
              )}
              </div>
            </div>
          </div>
        ))
      )}

      {showUpload && (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => setShowUpload(false)}>
    <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", maxHeight: "90vh", overflowY: "auto", border: "1px solid #2a2a2a" }} onClick={e => e.stopPropagation()}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Upload Your Music</div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 20 }}>Share your original music with the community</div>

      {/* Song title */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Song Title *</div>
        <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} placeholder="Enter your song title..."
          style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none" }} />
      </div>

      {/* Audio file */}
      <div style={{ marginBottom: 14 }}>
<div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Audio or Video File * (MP3, WAV, MP4, MOV)</div>
        <label style={{ display: "block", cursor: "pointer" }}>
          <div style={{ padding: 16, border: "2px dashed #2a2a2a", borderRadius: 10, textAlign: "center", background: uploadFile ? "rgba(29,185,84,0.05)" : "#0a0a0a", borderColor: uploadFile ? "#1db954" : "#2a2a2a" }}>
            {uploadFile ? (
              <div>
                <div style={{ fontSize: 24, marginBottom: 4 }}>🎵</div>
                <div style={{ fontSize: 13, color: "#1db954", fontWeight: 600 }}>{uploadFile.name}</div>
                <div style={{ fontSize: 11, color: "#555", marginTop: 3 }}>{(uploadFile.size / 1024 / 1024).toFixed(1)} MB</div>
              </div>
            ) : (
              <div>
<div style={{ fontSize: 28, marginBottom: 6 }}>{uploadFile?.type?.startsWith("video/") ? "🎬" : "🎵"}</div>
<div style={{ fontSize: 13, color: "#555" }}>Tap to select audio or video</div>
<div style={{ fontSize: 11, color: "#333", marginTop: 3 }}>MP3, WAV, MP4, MOV supported</div>
              </div>
            )}
          </div>
          <input type="file" accept=".mp3,.wav,.aac,.mp4,.mov,.m4v,audio/*,video/*" style={{ display: "none" }} onChange={e => setUploadFile(e.target.files[0])} />
        </label>
      </div>

      {/* Cover art */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Cover Art {uploadFile && !uploadFile.type?.startsWith("video/") ? <span style={{ color: "#e91429" }}>* Required</span> : "(Optional)"}
        </div>
        <label style={{ display: "inline-block", cursor: "pointer" }}>
          <div style={{ width: 80, height: 80, borderRadius: 10, border: "2px dashed #2a2a2a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0a0a" }}>
            {uploadCoverPreview ? <img src={uploadCoverPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 24 }}>🖼️</span>}
          </div>
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { setUploadCover(e.target.files[0]); setUploadCoverPreview(URL.createObjectURL(e.target.files[0])); }} />
        </label>
        <div style={{ fontSize: 11, color: "#444", marginTop: 6 }}>Tap to add cover image</div>
      </div>

      {uploadProgress && (
        <div style={{ background: "rgba(29,185,84,0.1)", border: "1px solid rgba(29,185,84,0.3)", color: "#1db954", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 12, textAlign: "center" }}>
          ⏳ {uploadProgress}
        </div>
      )}

      <button onClick={handleUploadSong} disabled={uploading}
        style={{ width: "100%", padding: 14, borderRadius: 50, border: "none", background: uploading ? "#333" : "#1db954", color: uploading ? "#666" : "#000", fontSize: 14, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer", marginBottom: 10 }}>
        {uploading ? "Uploading..." : "🚀 Upload to Feed"}
      </button>
      <button onClick={() => setShowUpload(false)} style={{ width: "100%", padding: 12, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer" }}>
        Cancel
      </button>
    </div>
  </div>
)}
      {/* Comments Modal */}
      {selectedSong && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 300, display: "flex", alignItems: "flex-end" }} onClick={() => setSelectedSong(null)}>
          <div style={{ background: "#141414", borderRadius: "20px 20px 0 0", padding: 20, width: "100%", maxHeight: "70vh", overflowY: "auto", border: "1px solid #2a2a2a" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 800, marginBottom: 16 }}>💬 Comments</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1, position: "relative" }}>
                <input value={newComment} onChange={e => {
                  setNewComment(e.target.value);
                  const val = e.target.value;
                  const atIndex = val.lastIndexOf("@");
                  if (atIndex !== -1 && atIndex === val.length - 1 || (atIndex !== -1 && !val.slice(atIndex + 1).includes(" "))) {
                    const query = val.slice(atIndex + 1).toLowerCase();
                    if (query.length >= 0) {
                      getDocs(collection(db, "artists")).then(snap => {
                        const results = snap.docs.map(d => d.data()).filter(a => (a.displayName || "").toLowerCase().includes(query)).slice(0, 4);
                        setMentionSuggestions(results);
                      });
                    }
                  } else {
                    setMentionSuggestions([]);
                  }
                }} placeholder="Add a comment... (@mention)" onKeyDown={e => e.key === "Enter" && postComment()} style={{ width: "100%", padding: "10px 14px", borderRadius: 50, border: "1px solid #2a2a2a", background: "#1a1a1a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                {mentionSuggestions.length > 0 && (
                  <div style={{ position: "absolute", bottom: "100%", left: 0, right: 0, background: "#1a1a1a", borderRadius: 12, border: "1px solid #2a2a2a", overflow: "hidden", marginBottom: 4 }}>
                    {mentionSuggestions.map((a, i) => (
                      <div key={i} onClick={() => {
                        const atIndex = newComment.lastIndexOf("@");
                        setNewComment(newComment.slice(0, atIndex) + `@${a.displayName} `);
                        setMentionSuggestions([]);
                        setMentionedUsers(prev => [...prev, a]);
                      }} style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#333", overflow: "hidden", flexShrink: 0 }}>
                          {a.photoUrl ? <img src={a.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>👤</span>}
                        </div>
                        <span style={{ fontSize: 13, color: "#fff" }}>@{a.displayName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={postComment} style={{ padding: "10px 16px", borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Post</button>
            </div>
            {comments.length === 0 ? <p style={{ color: "#555", fontSize: 13, textAlign: "center", padding: "20px 0" }}>No comments yet!</p> : comments.map(c => (
              <div key={c.id} style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#111", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>{c.userPhoto ? <img src={c.userPhoto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 14 }}>👤</span>}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{c.userName}</span>
                    <span style={{ fontSize: 11, color: "#444" }}>{timeAgo(c.createdAt)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#ccc", lineHeight: 1.5, marginBottom: 6 }}>{c.text}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <button onClick={async () => {
                      const likeRef = doc(db, "songs", selectedSong.id, "comments", c.id);
                      const liked = c.likedBy?.includes(user.uid);
                      const newLikedBy = liked ? c.likedBy.filter(id => id !== user.uid) : [...(c.likedBy || []), user.uid];
                      await updateDoc(likeRef, { likedBy: newLikedBy });
                      setComments(prev => prev.map(x => x.id === c.id ? { ...x, likedBy: newLikedBy } : x));
                    }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: c.likedBy?.includes(user.uid) ? "#e91429" : "#555", fontSize: 12 }}>
                      <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: c.likedBy?.includes(user.uid) ? "#e91429" : "none", stroke: c.likedBy?.includes(user.uid) ? "#e91429" : "#555", strokeWidth: 1.8 }}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                      {c.likedBy?.length || 0}
                    </button>
                    <button onClick={() => setReplyingTo(c)} style={{ background: "none", border: "none", cursor: "pointer", color: "#555", fontSize: 12 }}>Reply</button>
                  </div>
                  {/* Replies */}
                  {c.replies?.length > 0 && (
                    <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: "2px solid #1a1a1a" }}>
                      {c.replies.map((r, i) => (
                        <div key={i} style={{ marginBottom: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#fff", marginRight: 6 }}>{r.userName}</span>
                          <span style={{ fontSize: 12, color: "#aaa" }}>{r.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {replyingTo?.id === c.id && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={`Reply to ${c.userName}...`}
                        style={{ flex: 1, padding: "8px 12px", borderRadius: 20, border: "1px solid #2a2a2a", background: "#111", color: "#fff", fontSize: 12, outline: "none" }} />
                      <button onClick={async () => {
                        if (!replyText.trim()) return;
                        const reply = { userName: user.displayName || "User", userPhoto: user.photoURL || "", text: replyText.trim(), createdAt: new Date().toISOString() };
                        const newReplies = [...(c.replies || []), reply];
                        await updateDoc(doc(db, "songs", selectedSong.id, "comments", c.id), { replies: newReplies });
                        setComments(prev => prev.map(x => x.id === c.id ? { ...x, replies: newReplies } : x));
                        setReplyText("");
                        setReplyingTo(null);
                      }} style={{ background: "#1db954", border: "none", borderRadius: 20, padding: "8px 14px", color: "#000", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Send</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button onClick={() => setSelectedSong(null)} style={{ width: "100%", padding: 12, borderRadius: 10, border: "1px solid #2a2a2a", background: "transparent", color: "#666", fontSize: 14, cursor: "pointer", marginTop: 8 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}